import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftAddon,
  Input,
  InputRightAddon,
  Text,
  Button,
  Image,
  Flex,
  useToast,
  HStack,
  VStack,
} from "@chakra-ui/react";
import { Formik, Field } from "formik";
import {
  AttachmentIcon,
  CloseIcon,
  CheckIcon,
  CopyIcon,
} from "@chakra-ui/icons";
import { SketchPicker } from "react-color";
import {
  createWhiteLabel,
  updateWhiteLabel,
} from "@/services/whiteLabel.service";
import {
  generatePublicUrl,
  uploadFileToAzure,
} from "@/services/azureStorage.service";
import { isValidSiteName } from "@/utils/isValidNameSite";
import { WhiteLabel } from "@/models/whiteLabel.model";
import { useParams } from "react-router-dom";

interface WhiteLabelFormProps {
  onSubmit: (data: { whiteLabelId: string; whiteLabel: WhiteLabel }) => void;
  initialData?: any;
  isNew: boolean;
}

export default function WhiteLabelForm({
  onSubmit,
  initialData,
  isNew,
}: WhiteLabelFormProps) {
  const toast = useToast();
  const { id: routeId } = useParams();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [copied, setCopied] = useState({
    slug: false,
    formalizationLink: false,
  });

  const copyToClipboard = (
    text: string,
    field: "url" | "formalizationLink"
  ) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied((prev) => ({ ...prev, [field]: true }));
        toast({
          title: "URL copiada",
          description: "A URL foi copiada para a área de transferência.",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        setTimeout(
          () => setCopied((prev) => ({ ...prev, [field]: false })),
          2000
        );
      })
      .catch((err) => {
        console.error("Falha ao copiar texto: ", err);
      });
  };

  const initialValues = {
    slug: initialData?.slug || "",
    formalizationLink: initialData?.slug || "",
    primaryLogo: initialData?.primaryLogo || null,
    favicon: initialData?.favicon || null,
    primaryColor: initialData?.primaryColor || "#000000",
    secondaryColor: initialData?.secondaryColor || "#ffffff",
    creditPromoterId: initialData?.creditPromoterId,
  };

  const handleSubmit = async (
    values: typeof initialValues,
    { setSubmitting, resetForm }: any
  ) => {
    setSubmitting(true);

    try {
      const whiteLabelData: WhiteLabel = {
        id: initialData?.id,
        name: values.slug,
        slug: values.slug,
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
        primaryLogo: values.primaryLogo,
        favicon: values.favicon,
        creditPromoterId:
          values.creditPromoterId ??
          (location.pathname.startsWith("/promotoras/")
            ? Number(routeId)
            : undefined),
        secondaryLogo: "",
        corbanId: initialData?.corbanId,
      };

      if (
        values.primaryLogo &&
        values.primaryLogo !== initialData?.primaryLogo
      ) {
        const { uploadUrl: logoUploadUrl, publicUrl: logoPublicUrl } =
          await generatePublicUrl(values.primaryLogo.name, "documents");

        await uploadFileToAzure(logoUploadUrl, values.primaryLogo);

        whiteLabelData.primaryLogo = logoPublicUrl;
        values.primaryLogo = logoPublicUrl;
      }

      if (values.favicon && values.favicon !== initialData?.favicon) {
        const { uploadUrl: faviconUploadUrl, publicUrl: faviconPublicUrl } =
          await generatePublicUrl(values.favicon.name, "documents");

        await uploadFileToAzure(faviconUploadUrl, values.favicon);

        whiteLabelData.favicon = faviconPublicUrl;
        values.favicon = faviconPublicUrl;
      }

      const result = isNew
        ? await createWhiteLabel(whiteLabelData)
        : await updateWhiteLabel(whiteLabelData.id!, values);

      onSubmit({
        whiteLabelId: isNew ? result.id : whiteLabelData.id,
        whiteLabel: isNew ? result : whiteLabelData,
      });

      toast({
        title: `White Label ${isNew ? "criado" : "atualizado"} com sucesso!`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew) {
        resetForm();
        setLogoPreview(null);
        setFaviconPreview(null);
      }
    } catch (error) {
      console.error("Error creating/updating white label:", error);

      toast({
        title: `Erro ao ${isNew ? "criar" : "atualizar"} White Label`,
        description: `Ocorreu um erro ao ${isNew ? "criar" : "atualizar"} o White Label. Por favor, tente novamente.`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (initialData?.primaryLogo) {
      setLogoPreview(initialData.primaryLogo);
    }
    if (initialData?.favicon) {
      setFaviconPreview(initialData.favicon);
    }
  }, [initialData]);

  return (
    <Box w="100%">
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, isSubmitting, errors, handleSubmit }) => (
          <VStack spacing="2rem" width="100%">
            <Field name="slug">
              {({ field, form }: any) => (
                <FormControl width="100%">
                  <FormLabel
                    display="flex"
                    justifyContent="flex-start"
                    fontFamily="Lexend Deca"
                  >
                    Nome do seu site:
                  </FormLabel>
                  <HStack spacing="0.5rem" align="flex-start" width="100%">
                    <InputGroup
                      display="flex"
                      justifyContent="center"
                      width="100%"
                    >
                      <InputLeftAddon fontFamily="Lexend Deca">
                        https://
                      </InputLeftAddon>
                      <Input
                        {...field}
                        onChange={(e) => {
                          const lowerCaseValue = e.target.value.toLowerCase();
                          form.setFieldValue(field.name, lowerCaseValue);
                          if (isValidSiteName(lowerCaseValue)) {
                            form.setFieldValue(
                              "formalizationLink",
                              lowerCaseValue
                            );
                          }
                        }}
                        onBlur={async () => {
                          const isValid = await isValidSiteName(field.value);
                          if (!isValid) {
                            form.setFieldError(
                              field.name,
                              "Nome do site inválido"
                            );
                          } else {
                            form.setFieldValue(
                              "formalizationLink",
                              `${field.value.toLowerCase()}`
                            );
                          }
                        }}
                        onKeyDown={(e) => {
                          if (
                            !isValidSiteName(e.key) &&
                            e.key !== "Backspace" &&
                            e.key !== "Tab" &&
                            e.key !== "ArrowLeft" &&
                            e.key !== "ArrowRight"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        w="100%"
                        placeholder="Digite o nome do seu site"
                        fontFamily="Lexend Deca"
                      />
                      <InputRightAddon fontFamily="Lexend Deca" w="30%">
                        <Text>.corban.tec.br</Text>
                        {field.value &&
                          (form.errors.slug ? (
                            <CloseIcon color="red.500" ml="0.5rem" />
                          ) : (
                            <CheckIcon color="green.500" ml="0.5rem" />
                          ))}
                      </InputRightAddon>
                    </InputGroup>
                    <Button
                      leftIcon={<CopyIcon />}
                      onClick={() =>
                        copyToClipboard(
                          `https://${field.value}.corban.tec.br`,
                          "url"
                        )
                      }
                      colorScheme="green"
                      fontFamily="Lexend Deca"
                    >
                      {copied.slug ? "Copiado!" : "Copiar URL"}
                    </Button>
                  </HStack>
                  {form.errors.slug && (
                    <Text color="red.500" fontFamily="Lexend Deca" mt="0.5rem">
                      {form.errors.slug}
                    </Text>
                  )}
                </FormControl>
              )}
            </Field>

            <Field name="formalizationLink">
              {({ field }: any) => (
                <FormControl mt="1rem" width="100%">
                  <FormLabel
                    display="flex"
                    justifyContent="flex-start"
                    fontFamily="Lexend Deca"
                  >
                    Site de formalização
                  </FormLabel>
                  <HStack spacing="0.5rem" align="flex-start" width="100%">
                    <InputGroup
                      display="flex"
                      justifyContent="center"
                      width="100%"
                    >
                      <InputLeftAddon fontFamily="Lexend Deca">
                        https://
                      </InputLeftAddon>
                      <Input
                        {...field}
                        w="100%"
                        placeholder="Link de Formalização do seu site"
                        fontFamily="Lexend Deca"
                        readOnly
                        bg="gray.100"
                        _hover={{ bg: "gray.100" }}
                        _focus={{ bg: "gray.100", borderColor: "gray.300" }}
                        aria-readonly="true"
                      />
                      <InputRightAddon w="30%" fontFamily="Lexend Deca">
                        .minhabiometria.com.br
                      </InputRightAddon>
                    </InputGroup>
                    <Button
                      leftIcon={<CopyIcon />}
                      onClick={() =>
                        copyToClipboard(
                          `https://${field.value}.minhabiometria.com.br`,
                          "formalizationLink"
                        )
                      }
                      colorScheme="green"
                      fontFamily="Lexend Deca"
                    >
                      {copied.formalizationLink ? "Copiado!" : "Copiar URL"}
                    </Button>
                  </HStack>
                </FormControl>
              )}
            </Field>

            <Flex width="100%" justifyContent="space-evenly" flexWrap="wrap">
              <Box flex="1" minWidth="15rem" maxWidth="25rem" mb="1rem">
                <Field name="primaryLogo">
                  {() => (
                    <FormControl>
                      <FormLabel fontFamily="Lexend Deca">Logo</FormLabel>
                      <Flex direction="column" align="flex-start">
                        <Button
                          leftIcon={<AttachmentIcon />}
                          variant="outline"
                          onClick={() => {
                            document.getElementById("logoUpload")?.click();
                          }}
                        >
                          {values.primaryLogo
                            ? "Alterar Logo"
                            : "Selecionar Logo"}
                        </Button>
                        <Input
                          id="logoUpload"
                          type="file"
                          accept="image/*"
                          display="none"
                          onChange={(event) => {
                            const file = event.currentTarget.files?.[0] || null;
                            setFieldValue("primaryLogo", file);
                            setLogoPreview(
                              file ? URL.createObjectURL(file) : null
                            );
                          }}
                        />
                        {values.primaryLogo && (
                          <Text mt="0.5rem">{values.primaryLogo.name}</Text>
                        )}
                        {logoPreview && (
                          <Box
                            mt="1rem"
                            boxSize={{ base: "6.25rem", md: "9.375rem" }}
                            overflow="hidden"
                            borderRadius="md"
                            borderWidth="0.0625rem"
                          >
                            <Image
                              src={logoPreview}
                              alt="Logo Preview"
                              objectFit="contain"
                            />
                          </Box>
                        )}
                      </Flex>
                    </FormControl>
                  )}
                </Field>
              </Box>

              <Box flex="1" minWidth="15rem" maxWidth="25rem" mb="1rem">
                <Field name="favicon">
                  {() => (
                    <FormControl>
                      <FormLabel fontFamily="Lexend Deca">Favicon</FormLabel>
                      <Flex direction="column" align="flex-start">
                        <Button
                          leftIcon={<AttachmentIcon />}
                          variant="outline"
                          onClick={() => {
                            document.getElementById("faviconUpload")?.click();
                          }}
                        >
                          {values.favicon
                            ? "Alterar Favicon"
                            : "Selecionar Favicon"}
                        </Button>
                        <Input
                          id="faviconUpload"
                          type="file"
                          accept="image/*"
                          display="none"
                          onChange={(event) => {
                            const file = event.currentTarget.files?.[0] || null;
                            setFieldValue("favicon", file);
                            setFaviconPreview(
                              file ? URL.createObjectURL(file) : null
                            );
                          }}
                        />
                        {values.favicon && (
                          <Text mt="0.5rem">{values.favicon.name}</Text>
                        )}
                        {faviconPreview && (
                          <Box
                            mt="1rem"
                            boxSize={{ base: "2.5rem", md: "3.125rem" }}
                            overflow="hidden"
                            borderRadius="md"
                            borderWidth="0.0625rem"
                          >
                            <Image
                              src={faviconPreview}
                              alt="Favicon Preview"
                              objectFit="contain"
                            />
                          </Box>
                        )}
                      </Flex>
                    </FormControl>
                  )}
                </Field>
              </Box>
            </Flex>

            <Flex width="100%" justifyContent="space-evenly" flexWrap="wrap">
              <Box flex="1" minWidth="15rem" maxWidth="25rem" mb="1rem">
                <Field name="primaryColor">
                  {({ field, form }: any) => (
                    <FormControl>
                      <FormLabel fontFamily="Lexend Deca">
                        Cor Primária
                      </FormLabel>
                      <Box width="100%">
                        <SketchPicker
                          color={field.value}
                          onChangeComplete={(color) => {
                            form.setFieldValue("primaryColor", color.hex);
                          }}
                          width="100%"
                          disableAlpha
                        />
                      </Box>
                    </FormControl>
                  )}
                </Field>
              </Box>

              <Box flex="1" minWidth="15rem" maxWidth="25rem" mb="1rem">
                <Field name="secondaryColor">
                  {({ field, form }: any) => (
                    <FormControl>
                      <FormLabel fontFamily="Lexend Deca">
                        Cor Secundária
                      </FormLabel>
                      <Box width="100%">
                        <SketchPicker
                          color={field.value}
                          onChangeComplete={(color) => {
                            form.setFieldValue("secondaryColor", color.hex);
                          }}
                          width="100%"
                          disableAlpha
                        />
                      </Box>
                    </FormControl>
                  )}
                </Field>
              </Box>
            </Flex>

            <Button
              colorScheme="secondary"
              size="lg"
              isLoading={isSubmitting}
              alignSelf="center"
              px="2.5rem"
              fontFamily="Lexend Deca"
              onClick={() => handleSubmit()}
              isDisabled={Object.keys(errors).length > 0}
            >
              {isNew ? "Criar Whitelabel" : "Atualizar Whitelabel"}
            </Button>
          </VStack>
        )}
      </Formik>
    </Box>
  );
}
